import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Send, Upload, Mail, Phone, MapPin, CheckCircle } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const heroRef = useRef(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    project_type: '',
    timeline: '',
    message: '',
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Contact | Squarex Studios';

    const ctx = gsap.context(() => {
      // Hero animations
      gsap.fromTo(
        '.contact-hero-title',
        { opacity: 0, y: 80 },
        { opacity: 1, y: 0, duration: 1, ease: 'power4.out' }
      );
      gsap.fromTo(
        '.contact-hero-subtitle',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, delay: 0.2, ease: 'power3.out' }
      );

      // Form animation
      gsap.fromTo(
        '.contact-form',
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: formRef.current,
            start: 'top 80%',
          },
        }
      );
    });

    return () => ctx.revert();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch("https://formspree.io/f/mojnqwyn", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          project_type: '',
          timeline: '',
          message: '',
        });
        setFile(null);
      } else {
        const data = await response.json();
        if (Object.hasOwn(data, 'errors')) {
          setError(data.errors.map(error => error.message).join(", "));
        } else {
          setError("Oops! There was a problem submitting your form");
        }
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  const projectTypes = [
    'Shopify Development',
    'UI/UX Design',
    'Branding & Identity',
    'Video Production',
    'AI Photo Shoot (ShotLab)',
    'Full Brand Package',
    'Other',
  ];

  const timelines = [
    'ASAP',
    '1-2 weeks',
    '1 month',
    '2-3 months',
    'Flexible',
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-[50vh] flex items-center pt-32 pb-20 hero-gradient"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="contact-hero-subtitle text-[#af0000] font-mono text-sm uppercase tracking-widest mb-6">
            Contact Us
          </p>
          <h1 className="contact-hero-title text-5xl md:text-6xl lg:text-7xl font-bold font-['Syne'] tracking-tighter leading-[0.95] max-w-4xl">
            Let's start a{' '}
            <span className="text-[#af0000]">conversation</span>
          </h1>
          <p className="contact-hero-subtitle text-xl text-[#94a3b8] mt-8 max-w-2xl">
            Ready to transform your brand? Tell us about your project and let's explore what we can create together.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section ref={formRef} className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Form */}
            <div className="lg:col-span-2">
              {success ? (
                <div className="contact-form p-12 bg-white/5 border border-[#af0000]/30 text-center">
                  <div className="w-16 h-16 bg-[#af0000]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={32} className="text-[#af0000]" />
                  </div>
                  <h3 className="text-2xl font-bold font-['Syne'] mb-4">
                    Thank you for reaching out!
                  </h3>
                  <p className="text-[#94a3b8] mb-8">
                    We've received your inquiry and will get back to you within 24-48 hours.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="btn-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form space-y-6">
                  {/* Name & Email Row */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Name <span className="text-[#af0000]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-name-input"
                        className="form-input"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Email <span className="text-[#af0000]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-email-input"
                        className="form-input"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      data-testid="contact-company-input"
                      className="form-input"
                      placeholder="Your company name"
                    />
                  </div>

                  {/* Project Type */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Project Type <span className="text-[#af0000]">*</span>
                    </label>
                    <select
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleChange}
                      required
                      data-testid="contact-project-type-select"
                      className="form-input appearance-none cursor-pointer"
                    >
                      <option value="">Select a project type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Timeline */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Timeline <span className="text-[#af0000]">*</span>
                    </label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      required
                      data-testid="contact-timeline-select"
                      className="form-input appearance-none cursor-pointer"
                    >
                      <option value="">Select timeline</option>
                      {timelines.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Project Details <span className="text-[#af0000]">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      data-testid="contact-message-textarea"
                      className="form-input resize-none"
                      placeholder="Tell us about your project, goals, and any specific requirements..."
                    />
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Attach Files (Optional)
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        onChange={handleFileChange}
                        data-testid="contact-file-input"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      />
                      <div className="form-input flex items-center gap-3 cursor-pointer hover:border-[#af0000] transition-colors">
                        <Upload size={20} className="text-[#64748b]" />
                        <span className="text-[#64748b]">
                          {file ? file.name : 'Upload brief, mockups, or reference files'}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-[#64748b] mt-2">
                      Accepted formats: PDF, DOC, DOCX, PNG, JPG (Max 10MB)
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[#af0000] mb-4">
                  Get in Touch
                </h3>
                <div className="space-y-4">
                  <a
                    href="mailto:hello@squarexstudios.com"
                    className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 hover:border-[#af0000]/30 transition-all"
                  >
                    <div className="w-10 h-10 bg-[#af0000]/20 flex items-center justify-center">
                      <Mail size={18} className="text-[#af0000]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748b]">Email</p>
                      <p className="text-[#f8fafc]">hello@sqrx.in</p>
                    </div>
                  </a>

                  <a
                    href="tel:+919971140930"
                    className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 hover:border-[#af0000]/30 transition-all"
                  >
                    <div className="w-10 h-10 bg-[#af0000]/20 flex items-center justify-center">
                      <Phone size={18} className="text-[#af0000]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748b]">Phone</p>
                      <p className="text-[#f8fafc]">+91 9971140930</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4 p-4 bg-white/5 border border-white/10">
                    <div className="w-10 h-10 bg-[#af0000]/20 flex items-center justify-center flex-shrink-0">
                      <MapPin size={18} className="text-[#af0000]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748b]">Location</p>
                      <p className="text-[#f8fafc]">
                        Noida, Uttar Pradesh<br />
                        Lajpat Nagar, New Delhi
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[#af0000] mb-4">
                  Office Hours
                </h3>
                <div className="p-4 bg-white/5 border border-white/10">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#64748b]">Monday - Friday</span>
                      <span className="text-[#f8fafc]">11:00 AM - 7:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748b]">Saturday</span>
                      <span className="text-[#f8fafc]">11:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748b]">Sunday</span>
                      <span className="text-[#f8fafc]">Closed</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[#af0000] mb-4">
                  Response Time
                </h3>
                <div className="p-4 bg-white/5 border border-white/10">
                  <p className="text-[#94a3b8] text-sm">
                    We typically respond to inquiries within 24-48 business hours. For urgent matters, please call us directly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
